/* =============================================================================
 * Estado da conexao e preferencias do modo offline.
 *
 * TRES COISAS DIFERENTES, e confundi-las e' a origem de todo bug de offline:
 *
 *   estadoConexao ...... a verdade OBSERVADA. So' muda por evento do navegador
 *                        ou por resultado real de uma chamada. Vale sempre,
 *                        inclusive para quem nunca ligou o recurso - e' ela que
 *                        acende a faixa "sem conexao" da casca.
 *   recursoAtivo ....... o CONSENTIMENTO da pessoa (a page usuario/offline).
 *                        Sem ele nada e' guardado no aparelho e nada e' servido
 *                        de la': a chamada vai a' rede e falha normalmente.
 *   modoManual ......... "trabalhar offline agora", escolha explicita. A falta
 *                        de internet produz o MESMO efeito sem gravar nada -
 *                        ver `estaOffline`.
 *
 * `navigator.onLine` e' otimista: num Wi-Fi sem internet ele devolve true. Por
 * isso o estado deduzido das chamadas conta tanto quanto o evento do navegador.
 *
 * Fica em shared/offline e nao em shared/sessao de proposito: nao e' estado de
 * quem esta' logado - o Futigol e' publico, e um visitante deslogado usa o modo
 * offline igual.
 * ========================================================================== */

import { persistente } from '@shared/lib/armazenamento.js';
import { emitir, EVENTOS } from '@shared/lib/eventos.js';

const CHAVE = 'offline';

const estado = {
    conexao: 'online',
    recursoAtivo: false,
    modoManual: false,
};

/** Le a preferencia gravada e passa a ouvir os eventos de rede do navegador. */
export function iniciarConexao() {
    const salvo = persistente.ler(CHAVE, null) ?? {};
    estado.recursoAtivo = salvo.recursoAtivo === true;
    //O modo manual so' existe com o recurso ligado: sem esta guarda ele ficaria
    //adormecido no aparelho e voltaria sozinho no dia em que a pessoa religasse
    //o recurso, sem ter pedido nada.
    estado.modoManual = estado.recursoAtivo && salvo.modoManual === true;
    estado.conexao = globalThis.navigator?.onLine === false ? 'offline' : 'online';

    globalThis.addEventListener?.('online', () => notificarEstadoConexao('online'));
    globalThis.addEventListener?.('offline', () => notificarEstadoConexao('offline'));
}

function gravarPreferencia() {
    persistente.gravar(CHAVE, { recursoAtivo: estado.recursoAtivo, modoManual: estado.modoManual });
}

/* ---- leitura ------------------------------------------------------------ */

/** "Sem internet" observado, independente da vontade da pessoa. */
export function semInternet() {
    try {
        return globalThis.navigator?.onLine === false || estado.conexao === 'offline';
    } catch {
        return false;
    }
}

/** O consentimento. Sem ele o modo offline inteiro fica fora do caminho. */
export function podeUsarOffline() {
    return estado.recursoAtivo === true;
}

/** "A aplicacao deve se comportar como offline AGORA." */
export function estaOffline() {
    return podeUsarOffline() && (estado.modoManual === true || semInternet());
}

/** Offline por decisao da pessoa, com internet disponivel. */
export function estaOfflinePorEscolha() {
    return podeUsarOffline() && estado.modoManual === true && semInternet() === false;
}

export function estadoConexao() {
    return estado.conexao;
}

export function modoOfflineManual() {
    return estado.modoManual === true;
}

/* ---- escrita ------------------------------------------------------------ */

/**
 * Chamado pelo ServicoAplicacao a cada resposta (e pelos eventos do navegador).
 * So' emite quando o estado MUDA - senao toda chamada repintaria a casca.
 */
export function notificarEstadoConexao(novo) {
    if (estado.conexao === novo) return;
    estado.conexao = novo;
    emitir(EVENTOS.CONEXAO_ALTERADA, { ...estado });
}

export function definirRecursoOfflineAtivo(ativo) {
    estado.recursoAtivo = ativo === true;
    //Desligar o recurso desliga junto o "trabalhar offline agora" - ver o
    //comentario de `iniciarConexao`.
    if (estado.recursoAtivo === false) estado.modoManual = false;
    gravarPreferencia();
    emitir(EVENTOS.CONEXAO_ALTERADA, { ...estado });
    return estado.recursoAtivo;
}

export function definirModoOfflineManual(ativo) {
    estado.modoManual = podeUsarOffline() && ativo === true;
    gravarPreferencia();
    emitir(EVENTOS.CONEXAO_ALTERADA, { ...estado });
    return estado.modoManual;
}

/* ---- sondagem ----------------------------------------------------------- */

//Contrapartida de bloquear as chamadas quando offline: se TODAS forem
//bloqueadas, nada mais descobre que a rede voltou. De tempos em tempos uma
//passa para sondar; se ela suceder, o ServicoAplicacao marca 'online' e tudo
//volta ao normal sozinho.
const INTERVALO_SONDAGEM = 15_000;
let ultimaSondagem = 0;

export function permitirSondagem() {
    //Quem pediu para trabalhar offline nao quer que o app va' a' rede pelas
    //costas; e sem interface de rede a sonda so' gastaria tempo.
    if (estaOfflinePorEscolha()) return false;
    if (globalThis.navigator?.onLine === false) return false;

    const agora = Date.now();
    if (agora - ultimaSondagem < INTERVALO_SONDAGEM) return false;
    ultimaSondagem = agora;
    return true;
}
